import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  invalidateCustomerDocumentCategoryCache,
  useMoveAndDeleteDocumentCategory,
} from 'src/api/customer-document-category'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { ShowDialogProps } from 'src/show-dialog'
import { IDocumentCategory, IMoveAndDeleteDocumentCategory } from 'src/generated-types/document-category'
import { toast } from 'react-toastify'
import { Box, Button, FormControl, InputLabel, MenuItem, Select, FormControlLabel, Checkbox } from '@mui/material'
import { invalidateDocumentCategory } from 'src/api/users/document-category'

interface MoveAndDeleteDocumentCategoryDialogProps extends ShowDialogProps {
  id: EntityId
  customerId: EntityId
  customerDocumentCategories: IDocumentCategory[]
}

export const MoveAndDeleteDocumentCategoryDialog: React.FC<MoveAndDeleteDocumentCategoryDialogProps> = ({
  pop,
  id,
  customerId,
  customerDocumentCategories,
}) => {
  const { t: translate } = useTranslation(['documentcategory', 'common', 'common-api-message'])
  const mutation = useMoveAndDeleteDocumentCategory(customerId, id as EntityId)
  const [selectedCategoryId, setSelectedCategoryId] = useState<EntityId>(0)
  const availableCategories = customerDocumentCategories?.filter(cat => cat.id !== id) || []

  const onSubmit = useCallback(async () => {
    const moveAndDeleteDto: IMoveAndDeleteDocumentCategory = {
      targetCategoryId: selectedCategoryId,
      shouldDeleteAfterMove: true,
    }

    await mutation.mutateAsync(moveAndDeleteDto, {
      onSuccess: () => {
        toast.success(translate('successfullyRemoved', { ns: 'common-api-message' }))
        invalidateCustomerDocumentCategoryCache.getCustomerDocumentCategories(customerId)
        invalidateCustomerDocumentCategoryCache.getCustomerDocumentCategory(customerId, id)
        invalidateCustomerDocumentCategoryCache.getCustomerAndAdminDocumentCategories(customerId)
        invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)

        pop(true)
      },
      onError: error => {
        if (error && error['data'] === 'CannotMove') {
          toast.warning(translate('CannotMove', { ns: 'document-category' }))
        } else {
          toast.error(translate('InternalError', { ns: 'common-api-message' }))
        }
      },
    })
  }, [mutation, selectedCategoryId, translate, customerId, id, pop])

  return (
    <GenericDialogLayout title={translate('move-and-delete-document-category', { ns: 'document-category' })} pop={pop}>
      <Box sx={{ minWidth: 400, p: 1 }}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="target-category-select-label">
            {availableCategories.length === 0
              ? translate('no-available-categories', { ns: 'document-category' })
              : translate('select-target-category', { ns: 'document-category' })}
          </InputLabel>
          <Select
            labelId="target-category-select-label"
            value={selectedCategoryId || ''}
            label={
              availableCategories.length === 0
                ? translate('no-available-categories', { ns: 'document-category' })
                : translate('select-target-category', { ns: 'document-category' })
            }
            onChange={e => setSelectedCategoryId(e.target.value as number)}
            disabled={availableCategories.length === 0}
          >
            {availableCategories.map(category => (
              <MenuItem key={category.id} value={category.id}>
                {category.categoryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={true} disabled={true} color="primary" />}
          label={translate('delete-after-move', { ns: 'document-category' }) as string}
          sx={{ mt: 2, alignSelf: 'flex-start' }}
        />
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={() => pop(false)} sx={{ mt: 2, textTransform: 'none' }}>
            {translate('cancel', { ns: 'common' })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={!selectedCategoryId || mutation.isLoading}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            {translate('move-and-delete', { ns: 'document-category' })}
          </Button>
        </Box>
      </Box>
    </GenericDialogLayout>
  )
}
